
import React, { useMemo, useState } from 'react';
import { format, isAfter, isBefore, addDays, parseISO } from 'date-fns';
import { useGetProcurements } from '@/hooks/use-procurement';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogClose
} from '@/components/ui/dialog';
import { 
  AlertTriangle, 
  Calendar, 
  Clock, 
  Eye, 
  DollarSign, 
  Download, 
  FileText, 
  FileSpreadsheet,
  Filter,
  Search
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Link } from 'react-router-dom';
import { Procurement } from '@/types/database.types';
import { calculateTotalAmount } from '@/utils/procurementCalculations';
import * as XLSX from 'xlsx';

type FilterDateRange = {
  from: Date | undefined;
  to: Date | undefined;
};

interface FilterOptions {
  dateRange: FilterDateRange;
  urgency: string | null;
  sourceType: string | null;
}

const FinanceUpcomingPayments: React.FC = () => {
  const { data: procurements, isLoading } = useGetProcurements();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterOptions, setFilterOptions] = useState<FilterOptions>({
    dateRange: { from: undefined, to: undefined },
    urgency: null,
    sourceType: null
  });

  // Filter procurements with payment_due_date and pending payment
  const upcomingPayments = useMemo(() => {
    if (!procurements) return [];
    
    let filtered = (procurements as Procurement[]).filter(p => 
      p.finance?.payment_due_date && 
      !p.finance?.payment_confirmed &&
      ['pending_finance', 'pending_warehouse', 'price_confirmed', 'quality_confirmed', 
       'agreement_signed', 'pickup_scheduled', 'harvested', 'in_transit', 'delivered'].includes(p.status)
    );
    
    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(p =>
        (p.product_name && p.product_name.toLowerCase().includes(term)) ||
        (p.source_type && p.source_type.toLowerCase().includes(term)) ||
        (p.id && p.id.toLowerCase().includes(term))
      );
    }
    
    // Apply urgency filter
    if (filterOptions.urgency) {
      const now = new Date();
      filtered = filtered.filter(p => {
        if (!p.finance?.payment_due_date) return false;
        const dueDate = new Date(p.finance.payment_due_date);
        const daysDiff = Math.ceil((dueDate.getTime() - now.getTime()) / (1000 * 3600 * 24));
        
        switch (filterOptions.urgency) {
          case 'overdue':
            return daysDiff < 0;
          case 'today':
            return daysDiff === 0;
          case 'tomorrow':
            return daysDiff === 1;
          case 'week':
            return daysDiff >= 0 && daysDiff <= 7;
          case 'month':
            return daysDiff >= 0 && daysDiff <= 30;
          default:
            return true;
        }
      });
    }
    
    // Apply source type filter
    if (filterOptions.sourceType) {
      filtered = filtered.filter(p => p.source_type === filterOptions.sourceType);
    }
    
    // Apply date range filter
    if (filterOptions.dateRange.from || filterOptions.dateRange.to) {
      filtered = filtered.filter(p => {
        if (!p.finance?.payment_due_date) return false;
        const dueDate = parseISO(p.finance.payment_due_date.toString());
        const fromDate = filterOptions.dateRange.from;
        const toDate = filterOptions.dateRange.to;
        
        if (fromDate && toDate) {
          return isAfter(dueDate, fromDate) && isBefore(dueDate, toDate);
        } else if (fromDate) {
          return isAfter(dueDate, fromDate);
        } else if (toDate) {
          return isBefore(dueDate, toDate);
        }
        
        return true;
      });
    }
    
    // Sort by payment due date
    return filtered.sort((a, b) => {
      const dateA = new Date(a.finance!.payment_due_date!).getTime();
      const dateB = new Date(b.finance!.payment_due_date!).getTime();
      return dateA - dateB;
    });
  }, [procurements, searchTerm, filterOptions]);

  // Calculate summary statistics
  const summary = useMemo(() => {
    const now = new Date();
    const overdue = upcomingPayments.filter(p => {
      const dueDate = new Date(p.finance!.payment_due_date!);
      return isBefore(dueDate, now);
    });
    
    const dueToday = upcomingPayments.filter(p => {
      const dueDate = new Date(p.finance!.payment_due_date!);
      const today = new Date();
      return dueDate.toDateString() === today.toDateString();
    });
    
    const dueThisWeek = upcomingPayments.filter(p => {
      const dueDate = new Date(p.finance!.payment_due_date!);
      return isAfter(dueDate, now) && isBefore(dueDate, addDays(now, 7));
    });
    
    const totalAmount = upcomingPayments.reduce((sum, p) => sum + calculateTotalAmount(p), 0);
    const overdueAmount = overdue.reduce((sum, p) => sum + calculateTotalAmount(p), 0);
    
    return {
      total: upcomingPayments.length,
      overdue: overdue.length,
      dueToday: dueToday.length,
      dueThisWeek: dueThisWeek.length,
      totalAmount,
      overdueAmount
    };
  }, [upcomingPayments]);

  const getUrgencyBadge = (dueDate: string) => {
    const now = new Date();
    const due = new Date(dueDate);
    const daysDiff = Math.ceil((due.getTime() - now.getTime()) / (1000 * 3600 * 24));
    
    if (daysDiff < 0) {
      return <Badge variant="destructive">Overdue</Badge>;
    } else if (daysDiff === 0) {
      return <Badge variant="destructive">Due Today</Badge>;
    } else if (daysDiff === 1) {
      return <Badge variant="default">Due Tomorrow</Badge>;
    } else if (daysDiff <= 7) {
      return <Badge variant="secondary">Due This Week</Badge>;
    } else {
      return <Badge variant="outline">Upcoming</Badge>;
    }
  };

  const resetFilters = () => {
    setFilterOptions({
      dateRange: { from: undefined, to: undefined },
      urgency: null,
      sourceType: null
    });
  };

  const exportToCSV = () => {
    if (!upcomingPayments.length) return;
    
    const headers = ['ID', 'Product', 'Source Type', 'Due Date', 'Days Until Due', 'Amount', 'Status'];
    const csvRows = [headers];
    
    upcomingPayments.forEach(procurement => {
      const dueDate = new Date(procurement.finance!.payment_due_date!);
      const now = new Date();
      const daysDiff = Math.ceil((dueDate.getTime() - now.getTime()) / (1000 * 3600 * 24));
      
      csvRows.push([
        procurement.id,
        procurement.product_name || 'N/A',
        procurement.source_type,
        format(dueDate, 'yyyy-MM-dd'),
        daysDiff.toString(),
        calculateTotalAmount(procurement).toString(),
        procurement.status
      ]);
    });
    
    const csvContent = csvRows.map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    
    link.setAttribute('href', url);
    link.setAttribute('download', `upcoming_payments_${format(new Date(), 'yyyy-MM-dd')}.csv`);
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportToExcel = () => {
    if (!upcomingPayments.length) return;

    const wb = XLSX.utils.book_new();

    // Summary sheet
    const summaryData = [
      ['Upcoming Payments Report'],
      ['Generated on:', format(new Date(), 'yyyy-MM-dd HH:mm:ss')],
      ['Total Payments:', summary.total],
      ['Overdue:', summary.overdue],
      ['Due Today:', summary.dueToday],
      ['Due This Week:', summary.dueThisWeek],
      ['Total Amount:', `Br ${summary.totalAmount.toFixed(2)}`],
      ['Overdue Amount:', `Br ${summary.overdueAmount.toFixed(2)}`],
    ];

    const summaryWS = XLSX.utils.aoa_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(wb, summaryWS, 'Summary');

    // Detailed data sheet
    const detailedHeaders = [
      'ID', 'Product', 'Source Type', 'Due Date', 'Days Until Due', 
      'Amount', 'Status', 'Payment Term', 'Created Date'
    ];
    
    const detailedData = [detailedHeaders];
    
    upcomingPayments.forEach(procurement => {
      const dueDate = new Date(procurement.finance!.payment_due_date!);
      const now = new Date();
      const daysDiff = Math.ceil((dueDate.getTime() - now.getTime()) / (1000 * 3600 * 24));
      
      detailedData.push([
        procurement.id,
        procurement.product_name || 'N/A',
        procurement.source_type,
        format(dueDate, 'yyyy-MM-dd'),
        daysDiff.toString(),
        calculateTotalAmount(procurement).toFixed(2),
        procurement.status,
        procurement.finance?.payment_term_type || 'N/A',
        format(new Date(procurement.created_at), 'yyyy-MM-dd')
      ]);
    });

    const detailedWS = XLSX.utils.aoa_to_sheet(detailedData);
    XLSX.utils.book_append_sheet(wb, detailedWS, 'Detailed Data');

    XLSX.writeFile(wb, `upcoming_payments_${format(new Date(), 'yyyy-MM-dd')}.xlsx`);
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center space-x-4">
              <div className="p-2 bg-blue-100 rounded-full">
                <Calendar className="h-6 w-6 text-blue-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Payments</p>
                <h3 className="text-2xl font-bold">{summary.total}</h3>
                <p className="text-xs text-muted-foreground">Br {summary.totalAmount.toFixed(2)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center space-x-4">
              <div className="p-2 bg-red-100 rounded-full">
                <AlertTriangle className="h-6 w-6 text-red-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Overdue</p>
                <h3 className="text-2xl font-bold text-red-600">{summary.overdue}</h3>
                <p className="text-xs text-muted-foreground">Br {summary.overdueAmount.toFixed(2)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center space-x-4">
              <div className="p-2 bg-amber-100 rounded-full">
                <Clock className="h-6 w-6 text-amber-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Due Today</p>
                <h3 className="text-2xl font-bold text-amber-600">{summary.dueToday}</h3>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center space-x-4">
              <div className="p-2 bg-green-100 rounded-full">
                <DollarSign className="h-6 w-6 text-green-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Due This Week</p>
                <h3 className="text-2xl font-bold text-green-600">{summary.dueThisWeek}</h3>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Actions */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex flex-col sm:flex-row gap-2 flex-1">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search payments..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          
          <Select 
            value={filterOptions.urgency || "all"} 
            onValueChange={(value) => setFilterOptions(prev => ({ ...prev, urgency: value === "all" ? null : value }))}
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Filter by urgency" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Payments</SelectItem>
              <SelectItem value="overdue">Overdue</SelectItem>
              <SelectItem value="today">Due Today</SelectItem>
              <SelectItem value="tomorrow">Due Tomorrow</SelectItem>
              <SelectItem value="week">Due This Week</SelectItem>
              <SelectItem value="month">Due This Month</SelectItem>
            </SelectContent>
          </Select>
          
          <Select 
            value={filterOptions.sourceType || "all"} 
            onValueChange={(value) => setFilterOptions(prev => ({ ...prev, sourceType: value === "all" ? null : value }))}
          >
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Source type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Sources</SelectItem>
              <SelectItem value="farm">Farm</SelectItem>
              <SelectItem value="supplier">Supplier</SelectItem>
              <SelectItem value="agent">Agent</SelectItem>
              <SelectItem value="harvest">Harvest</SelectItem>
            </SelectContent>
          </Select>
          
          {(filterOptions.urgency || filterOptions.sourceType) && (
            <Button variant="outline" onClick={resetFilters}>
              Reset Filters
            </Button>
          )}
        </div>
        
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="gap-2">
              <Download className="h-4 w-4" />
              Export
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            <DropdownMenuItem onClick={exportToCSV}>
              <FileText className="h-4 w-4 mr-2" />
              Export as CSV
            </DropdownMenuItem>
            <DropdownMenuItem onClick={exportToExcel}>
              <FileSpreadsheet className="h-4 w-4 mr-2" />
              Export as Excel
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Payments Table */}
      {upcomingPayments.length === 0 ? (
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Calendar className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold">No upcoming payments</h3>
              <p className="text-muted-foreground mt-2">
                {searchTerm || filterOptions.urgency || filterOptions.sourceType
                  ? "No payments match your current filters."
                  : "There are no upcoming payments scheduled."
                }
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>Upcoming Payments</CardTitle>
            <CardDescription>
              {upcomingPayments.length} payment{upcomingPayments.length !== 1 ? 's' : ''} scheduled
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Due Date</TableHead>
                  <TableHead>ID</TableHead>
                  <TableHead>Product</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Urgency</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {upcomingPayments.map((procurement) => (
                  <TableRow key={procurement.id}>
                    <TableCell>
                      {format(new Date(procurement.finance!.payment_due_date!), 'MMM d, yyyy')}
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      {procurement.id.substring(0, 8)}...
                    </TableCell>
                    <TableCell>{procurement.product_name || 'N/A'}</TableCell>
                    <TableCell className="capitalize">{procurement.source_type}</TableCell>
                    <TableCell className="text-right font-medium">
                      Br {calculateTotalAmount(procurement).toFixed(2)}
                    </TableCell>
                    <TableCell>
                      {getUrgencyBadge(procurement.finance!.payment_due_date!)}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        asChild
                        title="View details"
                      >
                        <Link to={`/procurements/${procurement.id}`}>
                          <Eye className="h-4 w-4" />
                        </Link>
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default FinanceUpcomingPayments;
